import React, { useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { useNavigate } from 'react-router-dom';

import logo from '../assets/logo_no_bg.png';
import ActionButton from '../components/common/ActionButton';
import { signup, SignupPayload } from '../api/auth';
import { useProject } from '../context/ProjectContext';
import { ApiError } from '../utils/apiClient';
import { normalizeUserFacingError } from '../utils/normalizeUserFacingError';

type SignupField = 'organizationName' | 'username' | 'email' | 'password';
type SignupErrors = Partial<Record<SignupField, string>>;

const EMAIL_ERROR = 'Enter a valid email address, for example name@company.com.';
const SIGNUP_ERROR_MESSAGES: Record<string, string> = {
  email_already_registered: 'This email is already registered. Sign in or use a different email address.',
  verification_email_send_failed: 'Your account was created, but we could not send the verification email. Please try resending verification in a few minutes or contact support.',
  rate_limited: 'Too many signup attempts. Please wait a few minutes before trying again.',
  invalid_signup_request: 'Please fix the highlighted fields before creating your workspace.',
  internal_signup_error: 'We could not create your account because of a server problem. Please try again in a few minutes.',
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRules = [
  { label: 'minimum 8 characters', test: (value: string) => value.length >= 8 },
  { label: 'at least 1 uppercase letter', test: (value: string) => /[A-Z]/.test(value) },
  { label: 'at least 1 lowercase letter', test: (value: string) => /[a-z]/.test(value) },
  { label: 'at least 1 number', test: (value: string) => /\d/.test(value) },
  { label: 'at least 1 symbol', test: (value: string) => /[^A-Za-z0-9]/.test(value) },
];

export default function SignupPage() {
  const [organizationName, setOrganizationName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState<Partial<Record<SignupField, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);

  const { setCurrentProject } = useProject();
  const navigate = useNavigate();

  const getPasswordError = (value: string) => {
    const missing = passwordRules
      .filter((rule) => !rule.test(value))
      .map((rule) => rule.label);

    return missing.length ? `Missing: ${missing.join(', ')}.` : '';
  };

  const validateSignup = (): SignupErrors => {
    const nextErrors: SignupErrors = {};

    if (!organizationName.trim()) {
      nextErrors.organizationName = 'Enter your organization name.';
    }

    if (!username.trim()) {
      nextErrors.username = 'Enter your full name or username.';
    }

    if (!email.trim() || !emailPattern.test(email.trim())) {
      nextErrors.email = EMAIL_ERROR;
    }

    const passwordError = getPasswordError(password);
    if (passwordError) {
      nextErrors.password = passwordError;
    }

    return nextErrors;
  };

  const validationErrors = validateSignup();
  const showFieldError = (field: SignupField) => Boolean((touched[field] || submitted) && validationErrors[field]);
  const fieldHelperText = (field: SignupField, helperText: string) =>
    showFieldError(field) ? validationErrors[field] : helperText;
  const markTouched = (field: SignupField) => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const getSignupErrorCode = (signupError: unknown): string | null => {
    if (signupError instanceof ApiError && signupError.body && typeof signupError.body === 'object') {
      const detail = (signupError.body as any).detail;
      if (detail && typeof detail.code === 'string') {
        return detail.code;
      }
      if (typeof (signupError.body as any).code === 'string') {
        return (signupError.body as any).code;
      }
    }

    return null;
  };

  const handleSignup = async () => {
    setSubmitted(true);
    const nextErrors = validateSignup();

    if (Object.keys(nextErrors).length > 0) {
      setError('Please fix the highlighted fields before creating your workspace.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload: SignupPayload = {
        organization_name: organizationName,
        username,
        email,
        password,
      };
      const response = await signup(payload);

      setCurrentProject(null);
      navigate('/verify-email/check', {
        state: {
          email: response.email,
          devVerificationUrl: response.dev_verification_url,
        },
      });
    } catch (signupError: any) {
      console.error('[SIGNUP] Error:', signupError);
      const signupErrorCode = getSignupErrorCode(signupError);
      const fallbackSignupMessage = 'Unable to create your account. Please check the fields and try again.';
      const safeMessage = signupErrorCode
        ? SIGNUP_ERROR_MESSAGES[signupErrorCode] || fallbackSignupMessage
        : normalizeUserFacingError(signupError, fallbackSignupMessage);
      setError(safeMessage === 'Enter a valid email address.' ? EMAIL_ERROR : safeMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      void handleSignup();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: 'linear-gradient(135deg, #0A0E14 0%, #13171F 100%)',
      }}
    >
      <Paper
        elevation={24}
        className="page-transition"
        sx={{
          width: '100%',
          maxWidth: 460,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: 'rgba(19, 23, 31, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(57, 255, 20, 0.22)',
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={logo}
                alt="Quantik Mind logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Create your Quantik Mind account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your workspace, then choose how you want to start.
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{
                bgcolor: 'rgba(255, 190, 92, 0.08)',
                border: '1px solid rgba(255, 190, 92, 0.18)',
              }}
            >
              {error}
            </Alert>
          )}

          <Stack spacing={2.25}>
            <TextField
              label="Organization Name"
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              onBlur={() => markTouched('organizationName')}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="organization"
              fullWidth
              error={showFieldError('organizationName')}
              helperText={fieldHelperText('organizationName', 'Use the workspace or company name your team will recognize.')}
            />
            <TextField
              label="Full Name or Username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              onBlur={() => markTouched('username')}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="name"
              fullWidth
              error={showFieldError('username')}
              helperText={fieldHelperText('username', 'Use your full name or a login name.')}
            />
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => markTouched('email')}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="email"
              fullWidth
              error={showFieldError('email')}
              helperText={fieldHelperText('email', 'For example name@company.com.')}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onBlur={() => markTouched('password')}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="new-password"
              fullWidth
              error={showFieldError('password')}
              helperText={fieldHelperText(
                'password',
                'Requires minimum 8 characters, at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 symbol.'
              )}
            />
          </Stack>

          <ActionButton
            size="large"
            onClick={() => void handleSignup()}
            disabled={loading}
            startIcon={<PersonAddAlt1Icon />}
            sx={{
              py: 1.15,
            }}
          >
            {loading ? 'Creating account...' : 'Create account'}
          </ActionButton>

          <ActionButton
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{ alignSelf: 'flex-start' }}
          >
            Already have an account? Sign in
          </ActionButton>
        </Stack>
      </Paper>
    </Box>
  );
}
